import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { AuthModal } from '../components/Auth/AuthModal';
import { useAuthStore } from '../store/authStore';
import glassTheme from '../styles/glassTheme';

const PageContainer = styled.div`
  min-height: 100vh;
  ${glassTheme.gridBackground}
  position: relative;
  overflow-x: hidden;
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) translateX(0) scale(1); }
  25% { transform: translateY(-100px) translateX(50px) scale(1.1); }
  50% { transform: translateY(-50px) translateX(-30px) scale(0.9); }
  75% { transform: translateY(-150px) translateX(-50px) scale(1.05); }
`;

interface BubbleProps {
  style?: React.CSSProperties;
  delay?: string;
}

const Bubble = styled.div<BubbleProps>`
  position: fixed;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.1);
  backdrop-filter: blur(5px);
  animation: ${float} 20s infinite ease-in-out;
  pointer-events: none;
  animation-delay: ${props => props.delay || '0s'};
  z-index: 0;
`;

const Navigation = styled.nav`
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${glassTheme.colors.border};
  padding: 1rem 2rem;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: ${glassTheme.shadow.lg};
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const LogoText = styled.span`
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const LogoEmoji = styled.span`
  font-size: 2rem;
  filter: drop-shadow(0 0 10px rgba(96, 165, 250, 0.5));
`;

const NavActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const Button = styled.button<{ variant?: 'primary' | 'outline'; $active?: boolean }>`
  padding: 0.8rem 2rem;
  background: ${props => {
    if (props.$active) return glassTheme.gradients.cyber;
    return props.variant === 'outline'
      ? 'transparent'
      : glassTheme.gradients.primary;
  }};
  border: ${props => {
    if (props.$active) return `2px solid ${glassTheme.colors.accent.blue}`;
    return props.variant === 'outline'
      ? `2px solid ${glassTheme.colors.border}`
      : 'none';
  }};
  border-radius: ${glassTheme.borderRadius.full};
  color: ${glassTheme.colors.text.primary};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: ${props => props.$active ? `0 0 20px rgba(96, 165, 250, 0.6), ${glassTheme.shadow.glow}` : 'none'};
  transform: ${props => props.$active ? 'scale(1.05)' : 'none'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
    border-color: ${glassTheme.colors.borderHover};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 4rem 0;
  margin-bottom: 3rem;
`;

const glow = keyframes`
  from { filter: drop-shadow(0 0 20px rgba(255, 255, 255, 0.5)); }
  to { filter: drop-shadow(0 0 30px rgba(255, 255, 255, 0.8)); }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${glow} 2s ease-in-out infinite alternate;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.3rem;
  color: ${glassTheme.colors.text.secondary};
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const StatsSection = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 1.5rem;
  ${glassTheme.glass.light}
  transition: all ${glassTheme.transition.base};

  &:hover {
    transform: translateY(-5px);
    background: rgba(0, 0, 0, 0.6);
    box-shadow: ${glassTheme.shadow.glow};
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  background: ${glassTheme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: ${glassTheme.colors.text.secondary};
  font-size: 1.1rem;
`;

const FeaturesGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const FeatureCard = styled.div`
  ${glassTheme.glass.light}
  padding: 2rem;
  text-align: center;
  transition: all ${glassTheme.transition.base};

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.05);
    box-shadow: ${glassTheme.shadow.glow};
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  filter: drop-shadow(0 0 10px ${glassTheme.colors.accent.blue});
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  background: ${glassTheme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FeatureDescription = styled.p`
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.6;
`;

const CourseIntro = styled.section`
  ${glassTheme.glass.light}
  padding: 2rem;
  margin-bottom: 2rem;
`;

const CourseTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-size: 1.8rem;
`;

const CourseTitleIcon = styled.span`
  font-size: 2rem;
  filter: drop-shadow(0 0 10px ${glassTheme.colors.accent.blue});
`;

const CourseTitleText = styled.span`
  background: ${glassTheme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CourseList = styled.ul`
  list-style: none;
  padding-left: 0;
`;

const CourseItem = styled.li`
  padding: 0.5rem 0;
  color: ${glassTheme.colors.text.secondary};
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  line-height: 1.5;

  &:before {
    content: "✓";
    color: ${glassTheme.colors.accent.green};
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const LearningApproach = styled.section`
  ${glassTheme.glass.container}
  padding: 2rem;
  margin-bottom: 2rem;
`;

const ApproachTitle = styled.h3`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
  font-size: 1.8rem;
`;

const ApproachTitleIcon = styled.span`
  font-size: 2rem;
  filter: drop-shadow(0 0 10px ${glassTheme.colors.accent.blue});
`;

const ApproachTitleText = styled.span`
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ApproachGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const ApproachItem = styled.div`
  h4 {
    background: ${glassTheme.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 1rem;
  }

  p {
    color: ${glassTheme.colors.text.secondary};
    line-height: 1.6;
  }
`;

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [activeButton, setActiveButton] = useState<'login' | 'register' | null>(null);

  // Redirect authenticated users based on role
  useEffect(() => {
    if (isAuthenticated && user) {
      const primaryRole = user.primaryRole || user.roles?.[0]?.name || 'student';
      switch (primaryRole) {
        case 'admin':
        case 'super_admin':
          navigate('/admin/dashboard', { replace: true });
          break;
        case 'content_manager':
          navigate('/content-manager/dashboard', { replace: true });
          break;
        case 'student':
        default:
          navigate('/learn', { replace: true });
          break;
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleLogin = () => {
    setAuthMode('login');
    setActiveButton('login');
    setShowAuthModal(true);
  };

  const handleRegister = () => {
    setAuthMode('register');
    setActiveButton('register');
    setShowAuthModal(true);
  };

  const handleCloseAuth = () => {
    setShowAuthModal(false);
    // Keep the active button state to show which action was last clicked
  };

  useEffect(() => {
    // 检查URL hash
    const hash = window.location.hash.slice(1);
    if (hash === 'register') {
      setAuthMode('register');
      setShowAuthModal(true);
    } else if (hash === 'login') {
      setAuthMode('login');
      setShowAuthModal(true);
    }
  }, []);

  return (
    <PageContainer>
      {/* 导航栏 */}
      <Navigation>
        <NavContainer>
          <Logo>
            <LogoEmoji>🤖</LogoEmoji>
            <LogoText>StatQuest with AI</LogoText>
          </Logo>
          <NavActions>
            <Button variant="outline" onClick={handleLogin} $active={activeButton === 'login'}>
              <i className="fas fa-sign-in-alt"></i>
              登录
            </Button>
            <Button onClick={handleRegister} $active={activeButton === 'register'}>
              <i className="fas fa-user-plus"></i>
              注册
            </Button>
          </NavActions>
        </NavContainer>
      </Navigation>

      {/* 主内容 */}
      <Container>
        {/* Hero Section */}
        <HeroSection>
          <HeroTitle>StatQuest with AI - 借助AI探索统计学的世界</HeroTitle>
          <HeroDescription>
            专为心理学与行为科学设计的AI驱动统计学学习平台，通过智能助手辅助、实时代码执行、个性化学习路径，深入理解统计学原理，掌握R语言编程技能
          </HeroDescription>
          <Button onClick={handleRegister} style={{ fontSize: '1.2rem', padding: '1rem 2.5rem' }}>
            <i className="fas fa-rocket"></i>
            开始学习之旅
          </Button>
        </HeroSection>

        {/* 统计数据 */}
        <StatsSection>
          <StatItem>
            <StatNumber>100+</StatNumber>
            <StatLabel>核心统计知识元</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>200+</StatNumber>
            <StatLabel>R代码示例</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>24/7</StatNumber>
            <StatLabel>AI助手支持</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>1000+</StatNumber>
            <StatLabel>练习题目</StatLabel>
          </StatItem>
        </StatsSection>

        {/* 平台特性 */}
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>📚</FeatureIcon>
            <FeatureTitle>系统化学习</FeatureTitle>
            <FeatureDescription>
              从基础概念到高级应用，循序渐进地学习心理与行为统计学知识体系
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>💻</FeatureIcon>
            <FeatureTitle>R语言实践</FeatureTitle>
            <FeatureDescription>
              内置R代码编辑器，支持实时编译和执行，在实践中掌握统计分析技能
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>🤖</FeatureIcon>
            <FeatureTitle>AI智能助手</FeatureTitle>
            <FeatureDescription>
              基于深度学习的AI助手，为您提供个性化的学习指导和问题解答
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>📊</FeatureIcon>
            <FeatureTitle>数据可视化</FeatureTitle>
            <FeatureDescription>
              丰富的图表展示，让复杂的数据分析结果变得直观易懂
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>🎯</FeatureIcon>
            <FeatureTitle>实践案例</FeatureTitle>
            <FeatureDescription>
              基于真实心理学研究的案例分析，理论与实践相结合
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>📈</FeatureIcon>
            <FeatureTitle>学习进度追踪</FeatureTitle>
            <FeatureDescription>
              智能学习系统，记录您的学习进度，提供个性化的学习建议
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>

        {/* 课程介绍 */}
        <CourseIntro>
          <CourseTitle>
            <CourseTitleIcon>🎓</CourseTitleIcon>
            <CourseTitleText>课程涵盖内容</CourseTitleText>
          </CourseTitle>
          <CourseList>
            <CourseItem>描述性统计学：均值、中位数、标准差、方差等基础概念</CourseItem>
            <CourseItem>推断性统计学：假设检验、置信区间、显著性检验</CourseItem>
            <CourseItem>相关分析与回归分析：变量关系建模与预测</CourseItem>
            <CourseItem>方差分析：多组数据比较与效应量计算</CourseItem>
            <CourseItem>非参数检验：当数据不满足正态分布假设时的统计方法</CourseItem>
            <CourseItem>心理测量学：信度、效度分析与应用</CourseItem>
            <CourseItem>R语言编程：数据处理、统计分析、可视化技术</CourseItem>
            <CourseItem>机器学习基础：在心理学研究中的应用</CourseItem>
          </CourseList>
        </CourseIntro>

        {/* 学习方法 */}
        <LearningApproach>
          <ApproachTitle>
            <ApproachTitleIcon>🚀</ApproachTitleIcon>
            <ApproachTitleText>为什么选择我们的学习平台？</ApproachTitleText>
          </ApproachTitle>
          <ApproachGrid>
            <ApproachItem>
              <h4>互动式学习体验</h4>
              <p>
                通过在线编程环境，您可以直接在网页上编写和执行R代码，实时查看分析结果，
                让学习过程更加直观和有趣。
              </p>
            </ApproachItem>
            <ApproachItem>
              <h4>个性化学习路径</h4>
              <p>
                根据您的基础和学习目标，AI助手会为您推荐合适的学习内容和练习题目，
                确保学习效率和效果。
              </p>
            </ApproachItem>
            <ApproachItem>
              <h4>实时答疑解惑</h4>
              <p>
                学习过程中遇到任何问题，都可以随时向AI助手提问，获得详细的解答和指导。
              </p>
            </ApproachItem>
          </ApproachGrid>
        </LearningApproach>
      </Container>

      {/* 动态背景气泡 */}
      <Bubble style={{ width: '80px', height: '80px', left: '10%', top: '20%' }} />
      <Bubble style={{ width: '120px', height: '120px', left: '70%', top: '60%' }} delay="-5s" />
      <Bubble style={{ width: '60px', height: '60px', left: '85%', top: '10%' }} delay="-10s" />
      <Bubble style={{ width: '100px', height: '100px', left: '25%', top: '70%' }} delay="-15s" />

      {/* 认证模态框 */}
      <AuthModal
        visible={showAuthModal}
        onClose={handleCloseAuth}
        initialView={authMode}
      />
    </PageContainer>
  );
};